import React, { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, TextField, Button,
  Chip, FormControlLabel, Switch, LinearProgress, IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { toast } from "react-toastify";
import { inviteUser, parseEmails, EMAIL_RE } from "./utils/inviteHelper";

const BLUE = "#1976d2", GREEN = "#12A150", RED = "#c0362c", MUTED = "#64748b";

// Bulk-invite prospects into a tenant. Each email gets a login account and an
// activation e-mail to set their own password — nothing to share manually.
export default function InviteUsersModal({ open, onClose, tenant, carrierCompany = null }) {
  const [text, setText] = useState("");
  const [asAdmin, setAsAdmin] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // [{email, ok, message}]
  const [progress, setProgress] = useState(0);

  const emails = useMemo(() => parseEmails(text), [text]);
  const valid = emails.filter((e) => EMAIL_RE.test(e));
  const invalid = emails.filter((e) => !EMAIL_RE.test(e));

  const reset = () => { setText(""); setAsAdmin(false); setResults(null); setProgress(0); };
  const handleClose = () => { if (!sending) { reset(); onClose(); } };

  const send = async () => {
    if (!valid.length) { toast.error("Add at least one valid email"); return; }
    setSending(true);
    setResults(null);
    setProgress(0);
    const out = [];
    for (let i = 0; i < valid.length; i++) {
      const email = valid[i];
      try {
        // The dialog's toggle grants tenant-scoped admin only — never the
        // global CrediArc isAdmin flag.
        const result = await inviteUser(tenant, { email, isTenantAdmin: asAdmin, carrierCompany });
        out.push({ email, ok: true, message: result?.alreadySent ? "Invitation already sent; no duplicate sent" : result?.platform === "carrier" ? "Carrier platform invite sent" : "Risk platform invite sent" });
      } catch (e) {
        const msg = e?.response?.data?.details?.message || e?.response?.data?.message || e?.message || "Failed";
        out.push({ email, ok: false, message: typeof msg === "string" ? msg : "Failed" });
      }
      setProgress(Math.round(((i + 1) / valid.length) * 100));
      setResults([...out]);
    }
    setSending(false);
    const okCount = out.filter((r) => r.ok).length;
    if (okCount) toast.success(`${okCount} invite${okCount === 1 ? "" : "s"} sent`);
    if (okCount < out.length) toast.warn(`${out.length - okCount} could not be invited`);
  };

  const done = results && !sending;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#0f172a", display: "flex", alignItems: "center", gap: 1 }}>
            <MarkEmailReadIcon sx={{ color: BLUE }} /> Invite users
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED }}>{tenant?.name} · each gets a login and an activation email</Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={sending}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!done && (
          <>
            <TextField
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"Paste prospect emails — one per line or comma-separated\njane@acme.com, ops@globex.com\ncfo@initech.com"}
              multiline minRows={5} fullWidth disabled={sending}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap" }}>
              {valid.length > 0 && <Chip size="small" label={`${valid.length} valid`} sx={{ height: 22, background: "#dcfce7", color: "#15803d", fontWeight: 700 }} />}
              {invalid.length > 0 && <Chip size="small" label={`${invalid.length} invalid`} sx={{ height: 22, background: "#fee2e2", color: RED, fontWeight: 700 }} />}
              <FormControlLabel sx={{ ml: "auto" }} control={<Switch checked={asAdmin} onChange={(e) => setAsAdmin(e.target.checked)} disabled={sending} />} label={<Typography sx={{ fontSize: 13 }}>Invite as tenant admin</Typography>} />
            </Box>
            {invalid.length > 0 && (
              <Typography sx={{ fontSize: 12, color: RED, mt: 0.5 }}>Skipping invalid: {invalid.slice(0, 4).join(", ")}{invalid.length > 4 ? "…" : ""}</Typography>
            )}
          </>
        )}

        {sending && (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 13, color: MUTED, mb: 0.5 }}>Sending invites… {progress}%</Typography>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 3 }} />
          </Box>
        )}

        {results && (
          <Box sx={{ mt: 2, maxHeight: "40vh", overflowY: "auto", border: "1px solid #eef2f7", borderRadius: "10px" }}>
            {results.map((r) => (
              <Box key={r.email} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, borderBottom: "1px solid #f5f7fb" }}>
                {r.ok ? <CheckCircleIcon sx={{ fontSize: 18, color: GREEN }} /> : <ErrorOutlineIcon sx={{ fontSize: 18, color: RED }} />}
                <Typography sx={{ fontSize: 13, color: "#0f172a", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.email}</Typography>
                <Typography sx={{ fontSize: 12, color: r.ok ? GREEN : RED }}>{r.message}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {done ? (
          <>
            <Button onClick={() => { reset(); }} sx={{ textTransform: "none" }}>Invite more</Button>
            <Button onClick={handleClose} variant="contained" sx={{ textTransform: "none", background: BLUE }}>Done</Button>
          </>
        ) : (
          <>
            <Button onClick={handleClose} disabled={sending} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button onClick={send} variant="contained" disabled={sending || !valid.length} startIcon={<MarkEmailReadIcon />} sx={{ textTransform: "none", background: BLUE }}>
              {sending ? "Sending…" : `Send ${valid.length || ""} invite${valid.length === 1 ? "" : "s"}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
